// ============================================================
//  src/solicitud.js — Modal de solicitud de nuevo concurso
//
//  Permite al usuario solicitar el alta de un concurso que
//  no está en el catálogo. La solicitud se guarda en la
//  colección solicitudesConcurso con estado "pendiente".
//  El administrador la revisará desde el panel admin.
// ============================================================

import { db, auth }          from './firebase.js';
import { collection, addDoc,
         serverTimestamp }   from 'firebase/firestore';
import { notificarUsuarioSolicitudRecibida } from './emails.js';
import { mostrarToast,
         toggleBoton }       from './ui.js';
import { t }                 from './i18n.js';

// ── Referencia a la colección ─────────────────────────────────

const colSolicitudes = collection(db, 'solicitudesConcurso');

// ============================================================
//  API pública
// ============================================================

/**
 * Abre el modal de solicitud con el nombre precargado.
 * @param {string} nombrePrecargado - Texto que escribió el usuario en la búsqueda
 */
export function abrirModalSolicitud(nombrePrecargado = '') {
    const modal = document.getElementById('modal-solicitud');
    if (!modal) return;

    _limpiarFormulario();

    // Precargar el nombre que escribió el usuario
    const campoNombre = document.getElementById('solicitud-nombre');
    if (campoNombre) campoNombre.value = nombrePrecargado.trim();

    modal.classList.add('modal--visible');
    setTimeout(() => {
        // Foco al campo web si el nombre ya está cargado, sino al nombre
        const foco = nombrePrecargado.trim()
            ? document.getElementById('solicitud-web')
            : document.getElementById('solicitud-nombre');
        foco?.focus();
    }, 50);
}

/**
 * Cierra el modal de solicitud y resetea el formulario.
 */
export function cerrarModalSolicitud() {
    const modal = document.getElementById('modal-solicitud');
    if (!modal) return;
    modal.classList.remove('modal--visible');
    _limpiarFormulario();
}

/**
 * Maneja el submit del formulario de solicitud.
 * @param {Event} evento
 */
export async function manejarSubmitSolicitud(evento) {
    evento.preventDefault();

    const datos = _validarFormulario();
    if (!datos) return;

    const usuario = auth.currentUser;
    if (!usuario) {
        mostrarToast(t('toast.sesion.expirada'), 'error');
        return;
    }

    toggleBoton('btn-enviar-solicitud', false, t('solicitud.btn.enviando'));

    try {
        await addDoc(colSolicitudes, {
            nombre:          datos.nombre,
            webUrl:          datos.webUrl,
            modos:           datos.modos,
            bandas:          datos.bandas,
            descripcion:     datos.descripcion,
            solicitadoPor:       usuario.uid,
            solicitadoNombre:    usuario.displayName || 'Operador',
            solicitadoEmail:     usuario.email       || '',
            solicitadoEn:        serverTimestamp(),
            estado:          'pendiente',
            revisadoPor:     null,
            revisadoEn:      null,
            comentarioAdmin: null,
        });

        console.log('[Solicitud] Solicitud enviada por:', usuario.uid);

        // Enviar correos en background (no bloquear al usuario)
        const datosUsuario = {
            displayName: usuario.displayName || 'Operador',
            email:       usuario.email       || '',
        };
        notificarUsuarioSolicitudRecibida(datos, datosUsuario);

        mostrarToast(t('solicitud.toast.enviada'), 'exito');
        cerrarModalSolicitud();

    } catch (error) {
        console.error('[Solicitud] Error al enviar solicitud:', error);
        mostrarToast(t('solicitud.toast.error'), 'error');
    } finally {
        toggleBoton('btn-enviar-solicitud', true, t('solicitud.btn.enviar'));
    }
}

// ============================================================
//  Validación
// ============================================================

function _validarFormulario() {
    _limpiarErrores();

    const nombre      = document.getElementById('solicitud-nombre')?.value.trim() ?? '';
    const webUrl      = document.getElementById('solicitud-web')?.value.trim()    ?? '';
    const descripcion = document.getElementById('solicitud-descripcion')?.value.trim() ?? '';

    // Leer checkboxes de modos y bandas
    const modos  = [...document.querySelectorAll('[name="solicitud-modos"]:checked')].map(cb => cb.value);
    const bandas = [...document.querySelectorAll('[name="solicitud-bandas"]:checked')].map(cb => cb.value);

    if (!nombre) {
        _mostrarError('solicitud-nombre', t('solicitud.error.nombre'));
        return null;
    }

    return { nombre, webUrl, modos, bandas, descripcion };
}

function _mostrarError(idCampo, mensaje) {
    const campo = document.getElementById(idCampo);
    if (campo) campo.classList.add('campo--error');
    const errorEl = document.getElementById(`error-${idCampo}`);
    if (errorEl) {
        errorEl.textContent = mensaje;
        errorEl.classList.add('visible');
    }
}

function _limpiarErrores() {
    document.querySelectorAll('#form-solicitud .campo--error')
        .forEach(el => el.classList.remove('campo--error'));
    document.querySelectorAll('#form-solicitud .campo-error-msg')
        .forEach(el => { el.textContent = ''; el.classList.remove('visible'); });
}

function _limpiarFormulario() {
    document.getElementById('form-solicitud')?.reset();
    _limpiarErrores();
}

function _esUrlValida(url) {
    try { new URL(url); return true; } catch { return false; }
}
